import * as React from 'react';

import debounce from 'lodash/debounce';
import useResizeObserver from 'use-resize-observer';

import { cn } from '../../../lib';
import { Tooltip } from '../overlays';

const MEANINGFUL_ENDING_WITH_LOWERCASE =
  /((\d+[\s.,-_%]?)|([A-Z]{1,3}[\s.,-_%]))(\w{0,5})?$/;
const MEANINGFUL_ENDING_WITHOUT_LOWERCASE = /(\d+[\s.,-_%]?)(\w{0,5})?$/;

export type MaybeTruncateMiddleProps = {
  text: string;
  className?: string;
  onPress?: React.MouseEventHandler;
  disableTooltip?: boolean;
};

export const MaybeTruncateMiddle = ({
  text,
  className,
  onPress,
  disableTooltip,
}: MaybeTruncateMiddleProps) => {
  const [shouldTruncateMiddle, setShouldTruncateMiddle] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const firstPartRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const { width: rootWidth } = useResizeObserver<HTMLDivElement>({
    ref: rootRef as React.RefObject<HTMLDivElement>,
  });
  const [secondPart, setSecondPart] = React.useState('');

  const firstPart = React.useMemo(
    () =>
      text.substring(
        0,
        text.length - (shouldTruncateMiddle ? secondPart.length : 0),
      ),
    [secondPart.length, text, shouldTruncateMiddle],
  );

  const debouncedResizingHandler = React.useCallback(
    debounce((text: string) => {
      const { offsetWidth = 0, scrollWidth = 0 } = firstPartRef.current ?? {};
      const hasLowerCaseChars = /[a-z]/.test(text);
      const meaningfulEndingRegex = hasLowerCaseChars
        ? MEANINGFUL_ENDING_WITH_LOWERCASE
        : MEANINGFUL_ENDING_WITHOUT_LOWERCASE;

      const meaningfulEnding = text.match(meaningfulEndingRegex)?.[0] ?? '';

      window.requestAnimationFrame(() => {
        setIsTruncated(scrollWidth > offsetWidth);
        setShouldTruncateMiddle(
          scrollWidth > offsetWidth && !!meaningfulEnding,
        );
        setSecondPart(meaningfulEnding);
      });
    }, 200),
    [firstPartRef],
  );

  React.useLayoutEffect(() => {
    debouncedResizingHandler(text);
  }, [text, rootWidth]);

  return (
    <Tooltip
      tip={text}
      isDisabled={!isTruncated || disableTooltip}
      wrapWithButton={false}
      className="@MaybeTruncateMiddle"
    >
      <div
        className={cn(className, 'flex whitespace-nowrap')}
        onClick={onPress}
      >
        <div
          ref={firstPartRef}
          className="w-px flex-grow overflow-clip text-ellipsis"
        >
          {firstPart}
        </div>
        {shouldTruncateMiddle && <div>{secondPart}</div>}
      </div>
    </Tooltip>
  );
};
